/**
 * CustomSearchConfigManagement Component
 * 
 * Full CRUD management for Bing Custom Search configurations via Azure ARM API.
 * 
 * Features:
 * - List all configurations with domains
 * - Create new configurations with allowed/blocked domains
 * - Edit existing configurations
 * - Delete configurations
 * - Domain boost levels (Normal, Demoted)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  DefaultButton,
  PrimaryButton,
  IconButton,
  TextField,
  Dropdown,
  IDropdownOption,
  Dialog,
  DialogType,
  DialogFooter,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  mergeStyleSets,
  FontWeights,
  Toggle,
  Panel,
  PanelType,
} from '@fluentui/react';
import {
  AddRegular,
  DeleteRegular,
  EditRegular,
  SearchRegular,
  GlobeRegular,
  SaveRegular,
  DismissRegular,
  ArrowSyncRegular,
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';

// Types
interface DomainEntry {
  domain: string;
  includeSubPages: boolean;
  boostLevel: string;
}

interface CustomSearchConfig {
  id: string;
  name: string;
  allowedDomains: DomainEntry[];
  blockedDomains: DomainEntry[];
  type?: string;
}

interface CustomSearchConfigManagementProps {
  onSelectConfig?: (configName: string | null) => void;
  selectionMode?: boolean;
  selectedConfigName?: string;
  onConfigChange?: () => void;  // Called when configs are created/updated/deleted
}

// Styles
const styles = mergeStyleSets({
  container: {
    padding: '20px',
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    border: '1px solid #e1e1e1',
    minHeight: '400px',
  },
  header: {
    marginBottom: '16px',
  },
  configCard: {
    padding: '16px',
    margin: '8px 0',
    border: '1px solid #e1e1e1',
    borderRadius: '8px',
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      borderColor: '#667eea',
    },
  },
  selectedCard: {
    borderColor: '#667eea',
    backgroundColor: '#f0f6ff',
  },
  domainList: {
    padding: '8px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    marginTop: '8px',
  },
  domainItem: {
    padding: '6px 10px',
    backgroundColor: 'white',
    borderRadius: '4px',
    marginBottom: '4px',
    border: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  domainTag: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
  },
  allowedTag: {
    backgroundColor: '#dff6dd',
    color: '#107c10',
  },
  blockedTag: {
    backgroundColor: '#fde7e9',
    color: '#a80000',
  },
  boostTag: {
    backgroundColor: '#deecf9',
    color: '#667eea',
    marginLeft: '8px',
  },
});

const boostOptions: IDropdownOption[] = [
  { key: '', text: 'Normal' },
  { key: 'Demoted', text: 'Demoted' },
];

const CustomSearchConfigManagement: React.FC<CustomSearchConfigManagementProps> = ({
  onSelectConfig,
  selectionMode = false,
  selectedConfigName,
  onConfigChange,
}) => {
  // State
  const [configs, setConfigs] = useState<CustomSearchConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Panel state for create/edit
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<CustomSearchConfig | null>(null);
  const [configName, setConfigName] = useState('');
  const [allowedDomains, setAllowedDomains] = useState<DomainEntry[]>([]);
  const [blockedDomains, setBlockedDomains] = useState<DomainEntry[]>([]);
  const [saving, setSaving] = useState(false);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Load configurations
  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch('/api/admin/agents/custom-search-configs');
      if (response.ok) {
        const data = await response.json();
        setConfigs(data.configs || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load configurations');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  // Open create panel
  const handleCreate = () => {
    setEditingConfig(null);
    setConfigName('');
    setAllowedDomains([{ domain: '', includeSubPages: true, boostLevel: '' }]);
    setBlockedDomains([]);
    setIsPanelOpen(true);
  };

  // Open edit panel
  const handleEdit = (config: CustomSearchConfig) => {
    setEditingConfig(config);
    setConfigName(config.name);
    setAllowedDomains(config.allowedDomains.length > 0 ? [...config.allowedDomains] : [{ domain: '', includeSubPages: true, boostLevel: '' }]);
    setBlockedDomains([...config.blockedDomains]);
    setIsPanelOpen(true);
  };

  // Save configuration
  const handleSave = async () => {
    if (!configName.trim()) {
      setError('Configuration name is required');
      return;
    }

    const validAllowed = allowedDomains.filter(d => d.domain.trim());
    const validBlocked = blockedDomains.filter(d => d.domain.trim());

    if (validAllowed.length === 0 && validBlocked.length === 0) {
      setError('At least one domain is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const method = editingConfig ? 'PUT' : 'POST';
      const url = editingConfig 
        ? `/api/admin/agents/custom-search-configs/${configName}`
        : '/api/admin/agents/custom-search-configs';

      const body = editingConfig 
        ? { allowedDomains: validAllowed, blockedDomains: validBlocked }
        : { name: configName, allowedDomains: validAllowed, blockedDomains: validBlocked };

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setSuccessMessage(editingConfig ? 'Configuration updated!' : 'Configuration created!');
        setIsPanelOpen(false);
        loadConfigs();
        onConfigChange?.();  // Notify parent of change
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save configuration');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Delete configuration
  const handleDelete = async () => {
    if (!configToDelete) return;

    try {
      setDeleting(true);
      const response = await authenticatedFetch(`/api/admin/agents/custom-search-configs/${configToDelete}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSuccessMessage('Configuration deleted!');
        setDeleteDialogOpen(false);
        setConfigToDelete(null);
        loadConfigs();
        onConfigChange?.();  // Notify parent of change
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete configuration');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete configuration');
    } finally {
      setDeleting(false);
    }
  };

  // Domain management helpers
  const addAllowedDomain = () => {
    setAllowedDomains([...allowedDomains, { domain: '', includeSubPages: true, boostLevel: '' }]);
  };

  const updateAllowedDomain = (index: number, field: keyof DomainEntry, value: any) => {
    const updated = [...allowedDomains];
    updated[index] = { ...updated[index], [field]: value };
    setAllowedDomains(updated);
  };

  const removeAllowedDomain = (index: number) => {
    setAllowedDomains(allowedDomains.filter((_, i) => i !== index));
  };

  const addBlockedDomain = () => {
    setBlockedDomains([...blockedDomains, { domain: '', includeSubPages: true, boostLevel: '' }]);
  };

  const updateBlockedDomain = (index: number, field: keyof DomainEntry, value: any) => {
    const updated = [...blockedDomains];
    updated[index] = { ...updated[index], [field]: value };
    setBlockedDomains(updated);
  };

  const removeBlockedDomain = (index: number) => {
    setBlockedDomains(blockedDomains.filter((_, i) => i !== index));
  };

  // Selection handler
  const handleConfigClick = (config: CustomSearchConfig) => {
    if (selectionMode && onSelectConfig) {
      if (selectedConfigName === config.name) {
        onSelectConfig(null);
      } else {
        onSelectConfig(config.name);
      }
    }
  };

  // Table columns
  const columns: IColumn[] = [
    {
      key: 'name',
      name: 'Configuration Name',
      fieldName: 'name',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onRender: (item: CustomSearchConfig) => (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <SearchRegular style={{ fontSize: 16, color: '#107c10' }} />
          <Text style={{ fontWeight: FontWeights.semibold }}>{item.name}</Text>
        </Stack>
      ),
    },
    {
      key: 'allowedDomains',
      name: 'Allowed Domains',
      minWidth: 200,
      maxWidth: 350,
      isResizable: true,
      onRender: (item: CustomSearchConfig) => (
        <Stack tokens={{ childrenGap: 4 }}>
          {item.allowedDomains.slice(0, 3).map((d, i) => (
            <Stack key={i} horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
              <span className={`${styles.domainTag} ${styles.allowedTag}`}>✓</span>
              <Text variant="small">{d.domain}</Text>
              {d.boostLevel && d.boostLevel !== '' && (
                <span className={`${styles.domainTag} ${styles.boostTag}`}>{d.boostLevel === 'SuperBoost' ? 'Super Boost' : d.boostLevel}</span>
              )}
            </Stack>
          ))}
          {item.allowedDomains.length > 3 && (
            <Text variant="small" style={{ color: '#605e5c' }}>+{item.allowedDomains.length - 3} more</Text>
          )}
        </Stack>
      ),
    },
    {
      key: 'blockedDomains',
      name: 'Blocked Domains',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onRender: (item: CustomSearchConfig) => (
        <Stack tokens={{ childrenGap: 4 }}>
          {item.blockedDomains.slice(0, 2).map((d, i) => (
            <Stack key={i} horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
              <span className={`${styles.domainTag} ${styles.blockedTag}`}>✗</span>
              <Text variant="small">{d.domain}</Text>
            </Stack>
          ))}
          {item.blockedDomains.length > 2 && (
            <Text variant="small" style={{ color: '#605e5c' }}>+{item.blockedDomains.length - 2} more</Text>
          )}
          {item.blockedDomains.length === 0 && (
            <Text variant="small" style={{ color: '#a19f9d' }}>None</Text>
          )}
        </Stack>
      ),
    },
    {
      key: 'actions',
      name: 'Actions',
      minWidth: 100,
      maxWidth: 100,
      onRender: (item: CustomSearchConfig) => (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            title="Edit"
            onClick={() => handleEdit(item)}
          />
          <IconButton
            iconProps={{ iconName: 'Delete' }}
            title="Delete"
            onClick={() => {
              setConfigToDelete(item.name);
              setDeleteDialogOpen(true);
            }}
          />
        </Stack>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className={styles.header}>
        <Stack>
          <Text variant="large" style={{ fontWeight: FontWeights.semibold }}>
            <SearchRegular style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Custom Search Configurations
          </Text>
          <Text variant="small" style={{ color: '#605e5c' }}>
            Manage Bing Custom Search domain configurations
          </Text>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <DefaultButton
            iconProps={{ iconName: 'Refresh' }}
            text="Refresh"
            onClick={loadConfigs}
          />
          {!selectionMode && (
            <PrimaryButton
              iconProps={{ iconName: 'Add' }}
              text="New Configuration"
              onClick={handleCreate}
            />
          )}
        </Stack>
      </Stack>

      {/* Messages */}
      {successMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() => setSuccessMessage(null)}
          styles={{ root: { marginBottom: 12 } }}
        >
          {successMessage}
        </MessageBar>
      )}
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => setError(null)}
          styles={{ root: { marginBottom: 12 } }}
        >
          {error}
        </MessageBar>
      )}

      {/* Content */}
      {loading ? (
        <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 200 } }}>
          <Spinner size={SpinnerSize.large} label="Loading configurations..." />
        </Stack>
      ) : configs.length === 0 ? (
        <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 200 } }}>
          <SearchRegular style={{ fontSize: 48, color: '#c8c6c4', marginBottom: 16 }} />
          <Text variant="large" style={{ color: '#605e5c' }}>No configurations found</Text>
          <Text variant="small" style={{ color: '#a19f9d', marginBottom: 16 }}>
            Create your first custom search configuration
          </Text>
          <PrimaryButton
            iconProps={{ iconName: 'Add' }}
            text="Create Configuration"
            onClick={handleCreate}
          />
        </Stack>
      ) : selectionMode ? (
        // Selection mode - cards
        <Stack tokens={{ childrenGap: 8 }}>
          {configs.map((config) => (
            <div
              key={config.name}
              className={`${styles.configCard} ${selectedConfigName === config.name ? styles.selectedCard : ''}`}
              onClick={() => handleConfigClick(config)}
            >
              <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
                <Stack>
                  <Text style={{ fontWeight: FontWeights.semibold }}>{config.name}</Text>
                  <Stack horizontal tokens={{ childrenGap: 8 }} style={{ marginTop: 8 }}>
                    <Text variant="small" style={{ color: '#107c10' }}>
                      ✓ {config.allowedDomains.length} allowed
                    </Text>
                    {config.blockedDomains.length > 0 && (
                      <Text variant="small" style={{ color: '#a80000' }}>
                        ✗ {config.blockedDomains.length} blocked
                      </Text>
                    )}
                  </Stack>
                </Stack>
                {selectedConfigName === config.name && (
                  <Text style={{ color: '#667eea', fontWeight: FontWeights.semibold }}>Selected</Text>
                )}
              </Stack>
            </div>
          ))}
        </Stack>
      ) : (
        // Management mode - table
        <DetailsList
          items={configs}
          columns={columns}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          isHeaderVisible={true}
        />
      )}

      {/* Create/Edit Panel */}
      <Panel
        isOpen={isPanelOpen}
        type={PanelType.medium}
        onDismiss={() => setIsPanelOpen(false)}
        headerText={editingConfig ? `Edit: ${editingConfig.name}` : 'New Configuration'}
        closeButtonAriaLabel="Close"
        onRenderFooterContent={() => (
          <Stack horizontal tokens={{ childrenGap: 8 }} style={{ padding: '16px 0' }}>
            <PrimaryButton
              text={saving ? 'Saving...' : 'Save'}
              onClick={handleSave}
              disabled={saving}
            />
            <DefaultButton
              text="Cancel"
              onClick={() => setIsPanelOpen(false)}
            />
          </Stack>
        )}
        isFooterAtBottom={true}
      >
        <Stack tokens={{ childrenGap: 20 }} style={{ padding: '20px 0' }}>
          {/* Config Name */}
          <TextField
            label="Configuration Name"
            value={configName}
            onChange={(_, v) => setConfigName(v || '')}
            disabled={!!editingConfig}
            required
            placeholder="e.g., microsoft-docs-search"
          />

          {/* Allowed Domains */}
          <Stack tokens={{ childrenGap: 8 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Text style={{ fontWeight: FontWeights.semibold }}>Allowed Domains</Text>
              <IconButton
                iconProps={{ iconName: 'Add' }}
                title="Add domain"
                onClick={addAllowedDomain}
              />
            </Stack>
            {allowedDomains.map((domain, index) => (
              <Stack key={index} horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                <TextField
                  label={index === 0 ? 'Domain' : undefined}
                  placeholder="example.com"
                  value={domain.domain}
                  onChange={(_, v) => updateAllowedDomain(index, 'domain', v || '')}
                  styles={{ root: { flex: 2 } }}
                />
                <Dropdown
                  label={index === 0 ? 'Boost' : undefined}
                  options={boostOptions}
                  selectedKey={domain.boostLevel}
                  onChange={(_, opt) => updateAllowedDomain(index, 'boostLevel', opt?.key as string || '')}
                  styles={{ root: { width: 100 } }}
                />
                <Toggle
                  label={index === 0 ? 'Subpages' : undefined}
                  checked={domain.includeSubPages}
                  onChange={(_, checked) => updateAllowedDomain(index, 'includeSubPages', checked)}
                />
                {allowedDomains.length > 1 && (
                  <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => removeAllowedDomain(index)}
                  />
                )}
              </Stack>
            ))}
          </Stack>

          {/* Blocked Domains */}
          <Stack tokens={{ childrenGap: 8 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Text style={{ fontWeight: FontWeights.semibold }}>Blocked Domains (Optional)</Text>
              <IconButton
                iconProps={{ iconName: 'Add' }}
                title="Add blocked domain"
                onClick={addBlockedDomain}
              />
            </Stack>
            {blockedDomains.length === 0 ? (
              <Text variant="small" style={{ color: '#605e5c' }}>
                No blocked domains. Click + to add.
              </Text>
            ) : (
              blockedDomains.map((domain, index) => (
                <Stack key={index} horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                  <TextField
                    label={index === 0 ? 'Domain' : undefined}
                    placeholder="spam.com"
                    value={domain.domain}
                    onChange={(_, v) => updateBlockedDomain(index, 'domain', v || '')}
                    styles={{ root: { flex: 2 } }}
                  />
                  <Toggle
                    label={index === 0 ? 'Subpages' : undefined}
                    checked={domain.includeSubPages}
                    onChange={(_, checked) => updateBlockedDomain(index, 'includeSubPages', checked)}
                  />
                  <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => removeBlockedDomain(index)}
                  />
                </Stack>
              ))
            )}
          </Stack>
        </Stack>
      </Panel>

      {/* Delete Confirmation Dialog */}
      <Dialog
        hidden={!deleteDialogOpen}
        onDismiss={() => setDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Delete Configuration',
          subText: `Are you sure you want to delete "${configToDelete}"? This action cannot be undone.`,
        }}
      >
        <DialogFooter>
          <PrimaryButton
            text={deleting ? 'Deleting...' : 'Delete'}
            onClick={handleDelete}
            disabled={deleting}
            style={{ backgroundColor: '#a80000', borderColor: '#a80000' }}
          />
          <DefaultButton text="Cancel" onClick={() => setDeleteDialogOpen(false)} />
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default CustomSearchConfigManagement;
